import React from "react";
import { Grid } from "@mui/material";
import WorkingHours from "./form-fields/working-hours";
import JobTitle from "./form-fields/job-title";
import Remote from "./form-fields/remote";
import CountryField from "./form-fields/country";
import RegionField from "./form-fields/region";
import Seniority from "./form-fields/seniority";
import Website from "./form-fields/website";
import request from "../../utils/request";
import { useAuth } from "../../components/providers/auth-context";
import { minLengthChecker, minValueChecker } from "../../utils/forms/utils";

const OrganisationalInfoEdit = ({ changes, setChanges, setSaveResult, stopEdit }, ref) => {
    const { useImperativeHandle } = React;

    const { user, updateUser } = useAuth();

    const [regions, setRegions] = React.useState();

    const [workingHoursPerWeek, setWorkingHoursPerWeek] = React.useState({
        initialValue: user.workingHoursPerWeek,
        name: "workingHoursPerWeek",
        value: user.workingHoursPerWeek,
        error: null,
        validation: [minValueChecker(1)],
    });

    const [jobTitle, setJobTitle] = React.useState({
        initialValue: user.jobTitle,
        name: "jobTitle",
        value: user.jobTitle,
        error: null,
        validation: [minLengthChecker(3)],
    });

    const [workPreferences, setWorkPreferences] = React.useState({
        initialValue: user.workPreferences,
        name: "workPreferences",
        value: user.workPreferences,
        error: null,
    });

    const [seniority, setSeniority] = React.useState({
        initialValue: user.seniority,
        name: "seniority",
        value: user.seniority,
        error: null,
    });

    const [country, setCountry] = React.useState({
        initialValue: user.location && user.location.country,
        name: "country",
        value: user.location && user.location.country,
        error: null,
    });

    const [region, setRegion] = React.useState({
        initialValue: user.location && user.location.region,
        name: "region",
        value: user.location && user.location.region,
        error: null,
    });

    const [website, setWebsite] = React.useState({
        initialValue: user.website,
        name: "website",
        value: user.website,
        error: null,
        validation: [minLengthChecker(3)],
    });

    const toCheck = [
        workingHoursPerWeek,
        jobTitle,
        country,
        region,
        seniority,
        workPreferences,
        website,
    ];

    const isObject = (value) =>
        typeof value === "object" && !Array.isArray(value) && value !== null;

    React.useEffect(
        () => {
            const changesToApply = toCheck.reduce((acc, check) => {
                let { value, initialValue } = check;
                if (Array.isArray(value) && isObject(value[0])) {
                    value = JSON.stringify(value);
                    initialValue = JSON.stringify(initialValue);
                }
                if (value !== initialValue) {
                    return { ...acc, [check.name]: true };
                }
                return { ...acc, [check.name]: false };
            }, {});
            setChanges({ ...changes, ...changesToApply });
        },
        toCheck.map((check) => check.value)
    );

    const onCountryChange = (selectedCountry) => {
        if (selectedCountry && selectedCountry.value) {
            import(`src/assets/locales/regions/${selectedCountry.value}`).then((value) =>
                setRegions(value.default)
            );
        } else {
            setRegions();
        }
    };

    React.useEffect(() => {
        onCountryChange(country);
    }, [country.value]);

    const setError = (values, setter) => (error) => {
        setter({ ...values, error });
    };

    const setValue = (values, setter) => (value) => {
        setter({ ...values, value });
    };

    const handleSubmit = async (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const formData = {};

        if (country.value || region.value)
            formData.location = { country: country.value, region: region.value };
        formData.workingHoursPerWeek = workingHoursPerWeek.value;
        formData.jobTitle = jobTitle.value;
        formData.website = website.value;
        const selectedPrefs = workPreferences.value
            .filter((pref) => pref.selected)
            .map((pref) => pref.title);
        if (selectedPrefs.length > 0) formData.workPreferences = selectedPrefs;
        formData.seniority = seniority.value;
        // setLoading(true);
        stopEdit();
        setSaveResult("pending");
        try {
            await request("/api/users/me", {
                method: "POST",
                body: JSON.stringify(formData),
            });
            await updateUser();
            setSaveResult("success");
        } catch (e) {
            setSaveResult("failure");
        }
    };

    useImperativeHandle(ref, () => ({
        handleSubmit() {
            handleSubmit();
        },
    }));

    return (
        <form onSubmit={handleSubmit}>
            <Grid container direction="row" spacing={1} justifyContent="space-between">
                <Grid item style={{ width: "50%" }}>
                    <Seniority
                        value={seniority.value}
                        setValue={setValue(seniority, setSeniority)}
                        error={seniority.error}
                        setError={setError(seniority, setSeniority)}
                    />
                </Grid>
                <Grid item style={{ width: "50%" }}>
                    <JobTitle
                        handleSubmit={handleSubmit}
                        handleCancel={stopEdit}
                        field={jobTitle}
                        setValue={setValue(jobTitle, setJobTitle)}
                        setError={setError(jobTitle, setJobTitle)}
                    />
                </Grid>
                <Grid item style={{ width: "50%" }}>
                    <WorkingHours
                        handleSubmit={handleSubmit}
                        handleCancel={stopEdit}
                        field={workingHoursPerWeek}
                        setValue={setValue(workingHoursPerWeek, setWorkingHoursPerWeek)}
                        setError={setError(workingHoursPerWeek, setWorkingHoursPerWeek)}
                    />
                </Grid>
                <Grid item style={{ width: "50%" }}>
                    <Remote
                        value={workPreferences.value}
                        setValue={setValue(workPreferences, setWorkPreferences)}
                        error={workPreferences.error}
                        setError={setError(workPreferences, setWorkPreferences)}
                    />
                </Grid>
                <Grid item style={{ width: "50%" }}>
                    <Website
                        handleSubmit={handleSubmit}
                        handleCancel={stopEdit}
                        field={website}
                        setValue={setValue(website, setWebsite)}
                        setError={setError(website, setWebsite)}
                    />
                </Grid>
                <Grid item style={{ width: "50%" }}>
                    <CountryField
                        value={country.value}
                        setValue={setValue(country, setCountry)}
                        error={country.error}
                        setError={setError(country, setCountry)}
                    />
                </Grid>
                <Grid item style={{ width: "50%" }}>
                    <RegionField
                        regions={regions}
                        value={region.value}
                        setValue={setValue(region, setRegion)}
                        error={region.error}
                        setError={setError(region, setRegion)}
                    />
                </Grid>
            </Grid>
        </form>
    );
};

export default React.forwardRef(OrganisationalInfoEdit);
